import React, {useEffect, useState} from "react";
import Topbar from "../../layout/Topbar";
import SettingsSidebar from "../SettingsSidebar";
import {connect} from "react-redux";
import CustomSnackbar from "../../alert/CustomSnackbar";
import {
    Box,
    Button, Switch, FormControlLabel, IconButton, Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    CssBaseline,
    Chip, Typography
} from "@mui/material";
import Spinner from "../../spinner/Spinner";
import {Add, DeleteOutline, EditOutlined, RestoreFromTrash, TrackChangesOutlined} from "@mui/icons-material";
import CustomEventForm from "./CustomEventForm";
import {customEventActions} from "../../../store/actions/customEventActions";
import AlertDialog from "../../alert/AlertDialog";
import {SettingsContentBox, SettingsRoot} from "../General";
import {alpha} from "@mui/material/styles";
import {getSettingsColors, SettingsEmptyState, settingsTableStyles} from "../SettingsDesign";

export function CustomEvents(props) {

    const {customEvent} = props.state;

    const [isDeleted, setIsDeleted] = useState(false);
    const [editEventID, setEditEventID] = useState(0);
    const [prompt, setPrompt] = useState(false);
    const [archiveItem, setArchiveItem] = useState(null);
    const [open, setOpen] = useState(false);

    const handleOnClose = () => {
        setEditEventID(0);
        setOpen(false);
    }
    const handleOnClick = () => setOpen(true);
    const handleViewArchived = (v) => {
        if (v === true) {
            setIsDeleted(true);
        } else {
            setIsDeleted(false);
        }
    };

    const handelEditEvent = (rowID) => {
        setEditEventID(rowID);
        handleOnClick();
    };

    const handleOnClickToPrompt = (row) => {
        setPrompt(true);
        setArchiveItem(row);
    };

    const handleOnClickToArchive = (confirm) => {
        if (!confirm) {
            setPrompt(false);
            setArchiveItem(null);
            return false;
        }

        props?.archive(archiveItem?.id);
        setPrompt(false);
        setArchiveItem(null);
    };

    const getChip = (events) => {
        let values = events?.split(",");
        return values?.map((v) => {
            return <Chip key={v} label={v} color="primary" size="small" variant="outlined"/>;
        });
    };

    useEffect(() => {
        props.fetch({isDeleted});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDeleted]);

    useEffect(() => {
        if (customEvent?.rerender) {
            props.fetch({isDeleted});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customEvent?.rerender]);

    return (
        <SettingsRoot>
            <CssBaseline/>
            <Topbar/>
            <SettingsSidebar {...props}/>
            <SettingsContentBox sx={{bgcolor: theme => getSettingsColors(theme).canvas}}>
                {props.state.alert.errorMsg !== "" && <CustomSnackbar message={props.state.alert.errorMsg}/>}
                {props.state.alert.successMsg !== "" && <CustomSnackbar message={props.state.alert.successMsg} severity="success"/>}
                {prompt && <AlertDialog question="Archive confirmation"
                    text="Are you sure you want to archive this custom conversion event?" confirm={handleOnClickToArchive}/>}
                {open && <CustomEventForm onClose={handleOnClose} open={open} editEventID={editEventID}/>}
                <Box sx={{maxWidth: 1280, mx: 'auto', px: {xs: 2, md: 4}, py: 3}}>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3}}>
                        <Box>
                            <Typography component="h1" sx={{fontSize: {xs: 24, md: 28}, fontWeight: 700, letterSpacing: '-0.5px'}}>
                                Custom Conversions
                            </Typography>
                            <Typography sx={{mt: 0.75, fontSize: 14, color: 'text.secondary', whiteSpace: 'normal', fontWeight: 400}}>
                                Define the events that matter to your campaigns.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<Add/>} onClick={handleOnClick} sx={{borderRadius: 2, px: 2, py: 1}}>
                            Create conversion
                        </Button>
                    </Box>
                    <Box sx={{display: 'flex', gap: 2, p: {xs: 2, md: 2.5}, mb: 3, borderRadius: 3,
                        border: 0, bgcolor: theme => getSettingsColors(theme).inset}}>
                        <TrackChangesOutlined sx={{color: 'primary.main', mt: 0.25, fontSize: 28, flexShrink: 0}}/>
                        <Box>
                            <Typography sx={{fontWeight: 600, mb: 0.5, whiteSpace: 'normal'}}>Track every step of your funnel</Typography>
                            <Typography sx={{fontSize: 13, color: 'text.secondary', lineHeight: 1.7, whiteSpace: 'normal', fontWeight: 400}}>
                                Track registrations, purchases, installs, deposits, and more with custom conversion events.
                                Compare their performance in your reports. Create up to 20 events, depending on your subscription plan.
                            </Typography>
                        </Box>
                    </Box>
                    <Paper sx={{border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden', bgcolor: theme => getSettingsColors(theme).card}}>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                            px: {xs: 2, md: 2.5}, py: 2}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Typography component="h2" sx={{fontSize: 16, fontWeight: 600}}>{isDeleted ? 'Archived conversions' : 'Conversion events'}</Typography>
                                {!customEvent?.isLoading && <Chip label={customEvent?.data?.length || 0} size="small"
                                    sx={{bgcolor: theme => getSettingsColors(theme).inset, color: 'text.secondary', fontWeight: 600}}/>}
                            </Box>
                            <FormControlLabel sx={{mr: 0, '& .MuiFormControlLabel-label': {fontSize: 13, color: 'text.secondary'}}}
                                control={<Switch size="small" checked={isDeleted} onChange={e => handleViewArchived(e.target.checked)} name="showArchived"/>}
                                label="View archived"/>
                        </Box>
                        <TableContainer sx={{position: 'relative', minHeight: 180}}>
                            {customEvent?.isLoading && <Spinner overComponentBox={true}/>}
                            <Table size="small" aria-label="Custom conversion events" sx={{...settingsTableStyles, minWidth: 620,
                                '& th:first-of-type, & td:first-of-type': {pl: 2.5},
                                '& th:last-of-type, & td:last-of-type': {pr: 2.5}, '& tbody tr:last-child td': {borderBottom: 0}}}>
                                <TableHead><TableRow>
                                    <TableCell sx={{width: 56}}>#</TableCell>
                                    <TableCell>Event title</TableCell>
                                    <TableCell>Event values</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {customEvent?.data?.map((row, idx) => <TableRow key={row.id}>
                                        <TableCell sx={{color: 'text.secondary'}}>{idx + 1}</TableCell>
                                        <TableCell sx={{fontWeight: 600, maxWidth: 300, overflowWrap: 'anywhere'}}>{row.name}</TableCell>
                                        <TableCell><Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, maxWidth: 440,
                                            '& .MuiChip-root': {borderRadius: '6px', bgcolor: theme => alpha(theme.palette.primary.main, 0.06)}}}>
                                            {getChip(row.events)}
                                        </Box></TableCell>
                                        <TableCell align="right">
                                            {!row.isDeleted ? <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 0.5}}>
                                                <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${row.name}`} onClick={() => handelEditEvent(row.id)}>
                                                    <EditOutlined fontSize="small"/>
                                                </IconButton></Tooltip>
                                                <Tooltip title="Archive"><IconButton size="small" color="error" aria-label={`Archive ${row.name}`} onClick={() => handleOnClickToPrompt(row)}>
                                                    <DeleteOutline fontSize="small"/>
                                                </IconButton></Tooltip>
                                            </Box> : <Tooltip title="Restore"><IconButton size="small" color="primary" aria-label={`Restore ${row.name}`} onClick={() => props.restore(row.id)}>
                                                <RestoreFromTrash fontSize="small"/>
                                            </IconButton></Tooltip>}
                                        </TableCell>
                                    </TableRow>)}
                                </TableBody>
                            </Table>
                            {!customEvent?.isLoading && !customEvent?.data?.length && <SettingsEmptyState
                                icon={TrackChangesOutlined}
                                title={isDeleted ? 'No archived conversions' : 'No conversion events yet'}
                                description={isDeleted ? 'Archived events will appear here.' : 'Create your first conversion event to start tracking your funnel.'}/>}
                        </TableContainer>
                    </Paper>
                </Box>
            </SettingsContentBox>
        </SettingsRoot>
    );

}

const mapStateToProps = (state) => {
    return {
        state: {
            alert: state.alert,
            customEvent: state.customEvent,
        }
    };
};

const mapDispatchToProps = {
    fetch: customEventActions.fetch,
    archive: customEventActions.archive,
    restore: customEventActions.restore,
    rerender: customEventActions.rerender
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomEvents);
